
import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LandingPageData } from "@/models/storeModel";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";

interface PreviewModeProps {
  landingPageData: LandingPageData;
  previewMode: 'desktop' | 'mobile';
}

export function PreviewMode({ landingPageData, previewMode }: PreviewModeProps) {
  const renderSection = (section: any) => {
    const themeColors = landingPageData.theme_colors;
    
    switch (section.type) {
      case 'logo':
        return (
          <div className="py-1 px-2 bg-white border-b !important">
            <div className="flex items-center justify-start">
              {section.content.logoUrl ? (
                <img 
                  src={section.content.logoUrl} 
                  alt="Logo" 
                  className="h-5 sm:h-6 md:h-8 lg:h-12"
                />
              ) : (
                <h1 className="text-sm sm:text-base md:text-lg lg:text-2xl font-bold" style={{ color: themeColors.primary }}>
                  {section.content.businessName}
                </h1>
              )}
            </div>
          </div>
        );

      case 'hero':
        return (
          <div 
            className="relative bg-cover bg-center text-white min-h-[40vh] sm:min-h-[50vh] md:min-h-[60vh] lg:min-h-[70vh] xl:min-h-[80vh] !p-2 !important"
            style={{
              backgroundImage: section.content.backgroundImage 
                ? `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.6)), url(${section.content.backgroundImage})`
                : `linear-gradient(135deg, ${themeColors.primary}, ${themeColors.secondary})`,
            }}
          >
            <div className="absolute inset-0 flex items-center justify-center p-2 !important">
              <div className="text-center max-w-[280px] sm:max-w-sm md:max-w-lg lg:max-w-2xl xl:max-w-4xl w-full">
                <h1 className="text-base sm:text-lg md:text-2xl lg:text-4xl xl:text-5xl font-bold mb-2 sm:mb-3 md:mb-4 lg:mb-6 leading-tight">
                  {section.content.title}
                </h1>
                <p className="text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl mb-3 sm:mb-4 md:mb-6 lg:mb-8 opacity-90 leading-relaxed">
                  {section.content.subtitle}
                </p>
                {section.content.ctaText && (
                  <Button 
                    size={previewMode === 'mobile' ? 'sm' : 'lg'}
                    style={{ backgroundColor: themeColors.accent }}
                    className="text-white text-xs sm:text-sm md:text-base px-3 sm:px-4 md:px-6 py-2 md:py-3"
                  >
                    {section.content.ctaText}
                  </Button>
                )}
              </div>
            </div>
          </div>
        );

      case 'about':
        return (
          <div className="py-4 sm:py-6 md:py-8 lg:py-12 p-2 !important">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 md:gap-6 lg:gap-8 items-center">
                <div className="order-1">
                  <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl font-bold mb-2 sm:mb-3 md:mb-4 lg:mb-6" style={{ color: themeColors.primary }}>
                    {section.content.title}
                  </h2>
                  <p className="text-xs sm:text-sm md:text-base lg:text-lg text-gray-600 leading-relaxed">
                    {section.content.description}
                  </p>
                </div>
                {section.content.image && (
                  <div className="order-2 w-full">
                    <img 
                      src={section.content.image} 
                      alt="About" 
                      className="w-full h-28 sm:h-32 md:h-40 lg:h-48 xl:h-56 object-cover rounded-lg shadow-lg"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'menu':
        return (
          <div className="py-4 sm:py-6 md:py-8 lg:py-12 p-2 bg-gradient-to-br from-gray-50 via-white to-gray-100 !important">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl font-bold text-center mb-3 sm:mb-4 md:mb-6 lg:mb-8" style={{ color: themeColors.primary }}>
                {section.content.title}
              </h2>
              <div className="max-w-xs sm:max-w-sm md:max-w-lg lg:max-w-2xl xl:max-w-4xl mx-auto">
                <Carousel 
                  className="w-full"
                  plugins={[
                    Autoplay({
                      delay: 4000,
                      stopOnInteraction: false,
                      stopOnMouseEnter: true,
                    }),
                  ]}
                  opts={{
                    align: "start",
                    loop: true,
                  }}
                >
                  <CarouselContent>
                    {section.content.items?.map((item: any, index: number) => (
                      <CarouselItem key={index}>
                        <div className="relative overflow-hidden rounded-lg sm:rounded-xl md:rounded-2xl shadow-md sm:shadow-lg md:shadow-xl group cursor-pointer">
                          <div className="relative h-40 sm:h-48 md:h-56 lg:h-64 xl:h-80 w-full">
                            <img 
                              src={item.image} 
                              alt={item.name}
                              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="text-center text-white p-2 sm:p-3 md:p-4 lg:p-6 transform transition-transform duration-500 group-hover:scale-105">
                                <h3 className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl font-bold mb-1 sm:mb-2 md:mb-3 drop-shadow-lg">
                                  {item.name}
                                </h3>
                                <p className="text-xs sm:text-sm md:text-base lg:text-lg mb-1 sm:mb-2 md:mb-3 opacity-90 leading-relaxed">
                                  {item.description}
                                </p>
                                <div className="inline-block bg-white/20 backdrop-blur-sm rounded-full px-2 sm:px-3 md:px-4 py-1 sm:py-2 border border-white/30">
                                  <span className="text-sm sm:text-base md:text-lg lg:text-xl font-bold" style={{ color: themeColors.accent }}>
                                    {item.price}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  <CarouselPrevious className="left-1 sm:left-2 bg-white/80 backdrop-blur-sm border-white/50 hover:bg-white/90 transition-all duration-300 w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8" />
                  <CarouselNext className="right-1 sm:right-2 bg-white/80 backdrop-blur-sm border-white/50 hover:bg-white/90 transition-all duration-300 w-5 h-5 sm:w-6 sm:h-6 md:w-8 md:h-8" />
                </Carousel>
              </div>
            </div>
          </div>
        );

      case 'reservations':
        return (
          <div className="py-4 sm:py-6 md:py-8 lg:py-12 p-2 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 !important">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl font-bold mb-2 sm:mb-3 md:mb-4 text-white">
                {section.content.title}
              </h2>
              <p className="text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl text-gray-300 mb-3 sm:mb-4 md:mb-6">
                {section.content.subtitle}
              </p>
              <Button 
                size={previewMode === 'mobile' ? 'sm' : 'lg'}
                className="text-white px-3 sm:px-4 md:px-6 py-2 sm:py-3 text-xs sm:text-sm md:text-base rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                style={{ backgroundColor: themeColors.accent }}
                onClick={() => {
                  if (section.content.reservationUrl) {
                    window.open(section.content.reservationUrl, '_blank');
                  }
                }}
              >
                {section.content.buttonText}
              </Button>
            </div>
          </div>
        );

      case 'offers':
        return (
          <div className="py-4 sm:py-6 md:py-8 lg:py-12 p-2 bg-gray-50 !important">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl font-bold text-center mb-3 sm:mb-4 md:mb-6 lg:mb-8" style={{ color: themeColors.primary }}>
                {section.content.title}
              </h2>
              <div className="grid gap-2 sm:gap-3 md:gap-4 lg:gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                {section.content.offers?.map((offer: any, index: number) => (
                  <Card key={index} className="overflow-hidden shadow-md hover:shadow-lg transition-shadow">
                    {offer.image && (
                      <img 
                        src={offer.image} 
                        alt={offer.title}
                        className="w-full h-20 sm:h-24 md:h-28 lg:h-32 xl:h-40 object-cover"
                      />
                    )}
                    <CardContent className="p-2 sm:p-3 md:p-4">
                      <h3 className="text-sm sm:text-base md:text-lg font-bold mb-1 sm:mb-2" style={{ color: themeColors.secondary }}>
                        {offer.title}
                      </h3>
                      <p className="text-xs sm:text-sm text-gray-600">
                        {offer.description}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        );

      case 'footer':
        return (
          <footer className="bg-gray-900 text-white py-4 sm:py-6 md:py-8 p-2 !important">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6">
                <div>
                  <h3 className="text-sm sm:text-base md:text-lg font-bold mb-2 sm:mb-3" style={{ color: themeColors.accent }}>
                    Contact Us
                  </h3>
                  <div className="space-y-1 text-gray-300 text-xs sm:text-sm">
                    <p>{section.content.contactInfo?.address}</p>
                    <p>{section.content.contactInfo?.phone}</p>
                    <p>{section.content.contactInfo?.email}</p>
                  </div>
                </div>
                <div>
                  <h3 className="text-sm sm:text-base md:text-lg font-bold mb-2 sm:mb-3" style={{ color: themeColors.accent }}>
                    Follow Us
                  </h3>
                  <div className="space-y-1">
                    {section.content.socialLinks?.facebook && (
                      <div>
                        <a 
                          href={section.content.socialLinks.facebook} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-gray-300 hover:text-white transition-colors flex items-center gap-1 text-xs sm:text-sm"
                        >
                          <span>📘</span> Facebook
                        </a>
                      </div>
                    )}
                    {section.content.socialLinks?.instagram && (
                      <div>
                        <a 
                          href={section.content.socialLinks.instagram} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-gray-300 hover:text-white transition-colors flex items-center gap-1 text-xs sm:text-sm"
                        >
                          <span>📷</span> Instagram
                        </a>
                      </div>
                    )}
                    {section.content.socialLinks?.twitter && (
                      <div>
                        <a 
                          href={section.content.socialLinks.twitter} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-gray-300 hover:text-white transition-colors flex items-center gap-1 text-xs sm:text-sm"
                        >
                          <span>🐦</span> Twitter
                        </a>
                      </div>
                    )}
                    {section.content.socialLinks?.linkedin && (
                      <div>
                        <a 
                          href={section.content.socialLinks.linkedin} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-gray-300 hover:text-white transition-colors flex items-center gap-1 text-xs sm:text-sm"
                        >
                          <span>💼</span> LinkedIn
                        </a>
                      </div>
                    )}
                    {section.content.socialLinks?.youtube && (
                      <div>
                        <a 
                          href={section.content.socialLinks.youtube} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-gray-300 hover:text-white transition-colors flex items-center gap-1 text-xs sm:text-sm"
                        >
                          <span>📺</span> YouTube
                        </a>
                      </div>
                    )}
                  </div>
                </div>
                <div className="sm:col-span-2 lg:col-span-1">
                  <h3 className="text-sm sm:text-base md:text-lg font-bold mb-2 sm:mb-3" style={{ color: themeColors.accent }}>
                    Hours
                  </h3>
                  <div className="space-y-1 text-gray-300 text-xs sm:text-sm">
                    <p>Mon - Thu: 11am - 10pm</p>
                    <p>Fri - Sat: 11am - 11pm</p>
                    <p>Sunday: 12pm - 9pm</p>
                  </div>
                </div>
              </div>
            </div>
          </footer>
        );

      case 'text':
        return (
          <div className="py-3 sm:py-4 md:py-6 p-2 !important">
            <div className="max-w-4xl mx-auto">
              <h3 className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold mb-2 sm:mb-3" style={{ color: themeColors.primary }}>
                {section.content.title}
              </h3>
              <p 
                className={`text-${section.content.alignment} text-xs sm:text-sm md:text-base leading-relaxed`}
                style={{ 
                  color: section.content.color 
                }}
              >
                {section.content.content}
              </p>
            </div>
          </div>
        );

      case 'button':
        return (
          <div className="py-3 sm:py-4 md:py-6 p-2 text-center !important">
            <Button 
              size={previewMode === 'mobile' ? 'sm' : 'lg'}
              style={{ backgroundColor: themeColors.primary }}
              className="text-xs sm:text-sm md:text-base"
            >
              {section.content.text}
            </Button>
          </div>
        );

      case 'image':
        return (
          <div className="py-3 sm:py-4 md:py-6 p-2 !important">
            <div className="max-w-4xl mx-auto text-center">
              {section.content.url && (
                <img 
                  src={section.content.url} 
                  alt={section.content.alt}
                  className="w-full h-auto rounded-lg shadow-lg"
                />
              )}
              {section.content.caption && (
                <p className="text-gray-600 mt-2 sm:mt-3 text-xs sm:text-sm">
                  {section.content.caption}
                </p>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="w-full h-full">
      <div 
        className={`mx-auto bg-white shadow-xl overflow-hidden transition-all p-2 !important ${
          previewMode === 'mobile' 
            ? 'max-w-sm' 
            : 'max-w-full'
        }`}
        style={{ minHeight: '100vh' }}
      >
        {landingPageData.sections
          .sort((a, b) => a.position - b.position)
          .map((section) => (
            <div key={section.id}>
              {renderSection(section)}
            </div>
          ))}
      </div>
    </div>
  );
}
